// Accordion panels listing completed tasks grouped by day.
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import Panel from '@/components/ui/Panel';
import { useTasksContext, TaskUIProvider } from '@/features/tasks';
import TaskItem from '@/features/tasks/components/items/TaskItemBase';
import { applyDashboardListFilters } from '@/features/dashboard/utils/dashboardListFilters';
import { useT } from '@/i18n/useT';

const PANEL_FLAT_CLASS = 'panel-flat-list';
/** Keeps the completed tab scannable — scroll inside instead of pushing chart off-screen. */
const COMPLETED_LIST_MAX_HEIGHT = 'min(360px, 42vh)';

const CompletedPanels = ({
  completedDays,
  openDay,
  setOpenDay,
  projectFilter,
}) => {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { filteredTasks, projectDisplayMap } = useTasksContext();
  const filteredTaskIds = useMemo(
    () => new Set((filteredTasks ?? []).map((task) => task.id)),
    [filteredTasks],
  );

  const uiContextValue = useMemo(
    () => ({
      view: 'dashboard',
      menuSurface: 'dashboard',
      indentationType: 'none',
      selectedTaskIds: [],
      isMultiSelectActive: false,
      isListFocused: false,
      onToggleInSelection: () => {},
      onStartAddingSubtask: () => {},
      onRebalance: () => {},
      duplicateOrderKeyTaskIds: new Set(),
      onSelectSingle: (taskId) =>
        navigate(`${location.pathname}?task=${taskId}`),
      onTaskClick: (taskId) => navigate(`${location.pathname}?task=${taskId}`),
    }),
    [navigate, location.pathname],
  );

  const filteredDays = completedDays
    .map((day, i) => {
      const tasks = applyDashboardListFilters(day.tasks, {
        filteredTaskIds,
        breakdownProjectFilter: projectFilter,
        projectDisplayMap,
      });
      return { ...day, tasks, originalIndex: i };
    })
    .filter((day) => day.tasks.length > 0);

  if (filteredDays.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'text.subtle' }}>
        {t(
          'dashboard.noCompleted',
          'Geen voltooide taken gevonden die aan de huidige filters voldoen.',
        )}
      </Box>
    );
  }

  return (
    <TaskUIProvider value={uiContextValue}>
      <Box
        data-testid="completed-panels-scroll"
        sx={{
          maxHeight: COMPLETED_LIST_MAX_HEIGHT,
          overflowY: 'auto',
          pr: 0.5,
        }}
      >
        {filteredDays.map((day) => {
          const isOpen = projectFilter ? true : openDay === day.originalIndex;
          return (
            <Panel
              key={day.dateStr || day.label}
              flat
              contentClassName={PANEL_FLAT_CLASS}
              title={day.label}
              count={day.tasks.length}
              open={isOpen}
              onToggle={() =>
                setOpenDay(
                  openDay === day.originalIndex ? null : day.originalIndex,
                )
              }
            >
              {day.tasks.map((task, index) => (
                <TaskItem
                  key={task.id ?? `${day.dateStr || day.label}-${index}`}
                  task={task}
                  showPlayButton={false}
                />
              ))}
            </Panel>
          );
        })}
      </Box>
    </TaskUIProvider>
  );
};

export default CompletedPanels;
